import {
  type ChangeEvent,
  type ReactElement,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from 'components/Modal/Modal';
import { getLocalIsoDate } from 'components/MonitoringAgent/utils/monitoringScheduleDateUtils';
import StepItem from 'components/StepItem/StepItem';
import { BUTTON_VARIANT } from 'constants/interfaces';

import {
  agentTypeByCode,
  enabledTemplatesForType,
  formatHourOfDay,
  MONITORING_AGENT_TYPES,
  SCHEDULE_FREQUENCIES,
  SCHEDULE_TIMEZONES,
} from './scheduleWatchAgents';
import {
  createWatchTopic,
  TOPIC_MATCH_KINDS,
} from './topicWatchAgents';

import type { WatchTopicMatchKind, WatchTowerMockState } from './types';

import styles from './scss/TargetedMarketing.module.scss';

const LAST_STEP = 5;

interface IWatchTowerCreateTopicDialog {
  open: boolean,
  state: WatchTowerMockState,
  onClose: () => void,
  onApply: (next: WatchTowerMockState) => void,
}

function parseCsv(value: string): string[] {
  return value.split(',').map((item) => item.trim()).filter(Boolean);
}

function WatchTowerCreateTopicDialog({
  open,
  state,
  onClose,
  onApply,
}: IWatchTowerCreateTopicDialog): ReactElement {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('');
  const [agentType, setAgentType] = useState(MONITORING_AGENT_TYPES[0].type);
  const [templateId, setTemplateId] = useState(0);
  const [briefMarkdown, setBriefMarkdown] = useState('');
  const [matchKinds, setMatchKinds] = useState<WatchTopicMatchKind[]>([]);
  const [anyKind, setAnyKind] = useState(false);
  const [countries, setCountries] = useState('');
  const [cities, setCities] = useState('');
  const [naceSections, setNaceSections] = useState('');
  const [hsPrefixes, setHsPrefixes] = useState('');
  const [productNeedles, setProductNeedles] = useState('');
  const [hourOfDay, setHourOfDay] = useState(6);
  const [timezone, setTimezone] = useState('Europe/Berlin');
  const [frequency, setFrequency] = useState<string>('daily');
  const [paused, setPaused] = useState(false);
  const [startDate, setStartDate] = useState(getLocalIsoDate);
  const [endDate, setEndDate] = useState('');
  const [noEndDate, setNoEndDate] = useState(true);

  const templates = enabledTemplatesForType(agentType);

  useEffect(() => {
    if (!open) {
      return;
    }
    const { type } = MONITORING_AGENT_TYPES[0];
    setStep(1);
    setTitle('');
    setAgentType(type);
    setTemplateId(0);
    setBriefMarkdown('');
    setMatchKinds([]);
    setAnyKind(false);
    setCountries('');
    setCities('');
    setNaceSections('');
    setHsPrefixes('');
    setProductNeedles('');
    setHourOfDay(6);
    setTimezone('Europe/Berlin');
    setFrequency('daily');
    setPaused(false);
    setStartDate(getLocalIsoDate());
    setEndDate('');
    setNoEndDate(true);
  }, [open]);

  const onAgentTypeChange = (type: string) => {
    setAgentType(type);
    setTemplateId(0);
  };

  const onToggleKind = (kind: WatchTopicMatchKind, checked: boolean) => {
    setMatchKinds((prev) => (
      checked ? [...prev, kind] : prev.filter((item) => item !== kind)
    ));
  };

  const canAdvance = () => {
    if (step === 1) {
      return Boolean(title.trim());
    }
    if (step === 2) {
      return Boolean(templateId);
    }
    if (step === 3) {
      return Boolean(briefMarkdown.trim());
    }
    if (step === 4) {
      return matchKinds.length > 0;
    }
    if (step === 5) {
      if (!startDate.trim()) {
        return false;
      }
      if (noEndDate) {
        return true;
      }
      return Boolean(endDate.trim()) && endDate >= startDate;
    }
    return true;
  };

  const onSave = () => {
    if (!canAdvance()) {
      return;
    }
    const typeMeta = agentTypeByCode(agentType);
    const result = createWatchTopic(state, {
      title,
      type: agentType,
      subType: typeMeta?.subType || '',
      agentConfigId: templateId,
      briefMarkdown,
      matchKinds,
      matchRulesJson: {
        anyKind: anyKind || undefined,
        countries: parseCsv(countries),
        cities: parseCsv(cities),
        naceSections: parseCsv(naceSections),
        hsPrefixes: parseCsv(hsPrefixes),
        productNeedles: parseCsv(productNeedles),
      },
      timezone,
      hourOfDay,
      frequency,
      paused,
      startDate: startDate.trim(),
      endDate: noEndDate ? null : endDate.trim(),
    });
    if (!result.ok) {
      toast.error(t(`targetedMarketing.topicError_${result.error}`));
      return;
    }
    onApply(result.state);
    toast.success(t('targetedMarketing.topicCreated'));
    onClose();
  };

  const onConfirm = () => {
    if (step < LAST_STEP) {
      setStep(step + 1);
      return;
    }
    onSave();
  };

  const confirmTitle = step < LAST_STEP
    ? t('targetedMarketing.createScheduleNext')
    : t('targetedMarketing.saveTopic');

  return (
    <Modal
      open={open}
      title={t('targetedMarketing.createTopic')}
      className={`${styles.modalWide} ${styles.wizardModal}`}
      onClose={onClose}
      onReject={onClose}
      onConfirm={onConfirm}
      confirmTitle={confirmTitle}
      closeTitle={t('common.cancel')}
      confirmVariant={BUTTON_VARIANT.primary}
      disabledConfirm={!canAdvance()}
      additionalBtnTitle={step > 1 ? t('targetedMarketing.createScheduleBack') : undefined}
      onActionAdditionalBtn={step > 1 ? () => setStep(step - 1) : undefined}
    >
      <div className={styles.wizardSteps}>
        <StepItem
          title={t('targetedMarketing.createTopicStepTitle')}
          step={1}
          done={step > 1}
          disabled={step < 1}
        />
        <StepItem
          title={t('targetedMarketing.createTopicStepType')}
          step={2}
          done={step > 2}
          disabled={step < 2}
        />
        <StepItem
          title={t('targetedMarketing.createTopicStepBrief')}
          step={3}
          done={step > 3}
          disabled={step < 3}
        />
        <StepItem
          title={t('targetedMarketing.createTopicStepMatch')}
          step={4}
          done={step > 4}
          disabled={step < 4}
        />
        <StepItem
          title={t('targetedMarketing.createTopicStepWhen')}
          step={5}
          done={step > 5}
          disabled={step < 5}
        />
      </div>

      {step === 1 ? (
        <div className={styles.formGrid}>
          <p className={`${styles.muted} ${styles.fieldWide}`}>
            {t('targetedMarketing.createTopicPickTitle')}
          </p>
          <div className={`${styles.field} ${styles.fieldWide}`}>
            <label htmlFor="wt-topic-title">{t('targetedMarketing.title')}</label>
            <input
              id="wt-topic-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
        </div>
      ) : null}

      {step === 2 ? (
        <>
          <fieldset className={`${styles.field} ${styles.fieldWide} ${styles.checkFieldset}`}>
            <legend>{t('targetedMarketing.createSchedulePickType')}</legend>
            <div className={styles.choiceList}>
              {MONITORING_AGENT_TYPES.map((item) => (
                <label
                  key={item.type}
                  className={`${styles.choiceCard} ${agentType === item.type ? styles.choiceCardActive : ''}`}
                  htmlFor={`wt-topic-type-${item.type}`}
                >
                  <input
                    id={`wt-topic-type-${item.type}`}
                    type="radio"
                    name="wt-topic-agent-type"
                    checked={agentType === item.type}
                    onChange={() => onAgentTypeChange(item.type)}
                  />
                  {item.label}
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset className={`${styles.field} ${styles.fieldWide} ${styles.checkFieldset}`}>
            <legend>{t('targetedMarketing.createSchedulePickTemplates')}</legend>
            {templates.length === 0 ? (
              <p className={styles.muted}>{t('targetedMarketing.noEnabledTemplate')}</p>
            ) : (
              <div className={styles.choiceList}>
                {templates.map((item) => (
                  <label
                    key={item.id}
                    className={`${styles.choiceCard} ${templateId === item.id ? styles.choiceCardActive : ''}`}
                    htmlFor={`wt-topic-tpl-${item.id}`}
                  >
                    <input
                      id={`wt-topic-tpl-${item.id}`}
                      type="radio"
                      name="wt-topic-template"
                      checked={templateId === item.id}
                      onChange={() => setTemplateId(item.id)}
                    />
                    {item.name}
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        </>
      ) : null}

      {step === 3 ? (
        <div className={styles.formGrid}>
          <p className={`${styles.muted} ${styles.fieldWide}`}>
            {t('targetedMarketing.createTopicPickBrief')}
          </p>
          <div className={`${styles.field} ${styles.fieldWide}`}>
            <label htmlFor="wt-topic-brief">{t('targetedMarketing.trainingBrief')}</label>
            <textarea
              id="wt-topic-brief"
              value={briefMarkdown}
              onChange={(event) => setBriefMarkdown(event.target.value)}
            />
          </div>
        </div>
      ) : null}

      {step === 4 ? (
        <>
          <fieldset className={`${styles.field} ${styles.fieldWide} ${styles.checkFieldset}`}>
            <legend>{t('targetedMarketing.createTopicPickMatch')}</legend>
            <div className={styles.choiceList}>
              {TOPIC_MATCH_KINDS.map((kind) => (
                <label
                  key={kind}
                  className={`${styles.choiceCard} ${matchKinds.includes(kind) ? styles.choiceCardActive : ''}`}
                  htmlFor={`wt-topic-kind-${kind}`}
                >
                  <input
                    id={`wt-topic-kind-${kind}`}
                    type="checkbox"
                    checked={matchKinds.includes(kind)}
                    onChange={(event) => onToggleKind(kind, event.target.checked)}
                  />
                  {t(`targetedMarketing.matchKind_${kind}`)}
                </label>
              ))}
            </div>
          </fieldset>
          <p className={`${styles.muted} ${styles.fieldWide}`}>
            {t('targetedMarketing.matchRulesHint')}
          </p>
          <div className={styles.formGrid}>
            <label className={`${styles.checkItem} ${styles.fieldWide}`} htmlFor="wt-topic-any-kind">
              <input
                id="wt-topic-any-kind"
                type="checkbox"
                checked={anyKind}
                onChange={(event) => setAnyKind(event.target.checked)}
              />
              {t('targetedMarketing.matchAnyKind')}
            </label>
            <div className={styles.field}>
              <label htmlFor="wt-topic-countries">{t('targetedMarketing.matchCountries')}</label>
              <input
                id="wt-topic-countries"
                value={countries}
                onChange={(event) => setCountries(event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="wt-topic-cities">{t('targetedMarketing.matchCities')}</label>
              <input
                id="wt-topic-cities"
                value={cities}
                onChange={(event) => setCities(event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="wt-topic-nace">{t('targetedMarketing.matchNace')}</label>
              <input
                id="wt-topic-nace"
                value={naceSections}
                onChange={(event) => setNaceSections(event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="wt-topic-hs">{t('targetedMarketing.matchHs')}</label>
              <input
                id="wt-topic-hs"
                value={hsPrefixes}
                onChange={(event) => setHsPrefixes(event.target.value)}
              />
            </div>
            <div className={`${styles.field} ${styles.fieldWide}`}>
              <label htmlFor="wt-topic-needles">{t('targetedMarketing.matchProductNeedles')}</label>
              <input
                id="wt-topic-needles"
                value={productNeedles}
                onChange={(event) => setProductNeedles(event.target.value)}
              />
            </div>
          </div>
        </>
      ) : null}

      {step === 5 ? (
        <div className={styles.formGrid}>
          <p className={`${styles.muted} ${styles.fieldWide}`}>
            {t('targetedMarketing.createTopicPickWhen')}
          </p>
          <div className={styles.whenWindow}>
            <div className={styles.field}>
              <label htmlFor="wt-topic-begin">{t('targetedMarketing.beginDate')}</label>
              <input
                id="wt-topic-begin"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="wt-topic-end">{t('targetedMarketing.endDate')}</label>
              <input
                id="wt-topic-end"
                type="date"
                min={startDate}
                value={noEndDate ? '' : endDate}
                disabled={noEndDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
              <label className={styles.checkItem} htmlFor="wt-topic-no-end">
                <input
                  id="wt-topic-no-end"
                  type="checkbox"
                  checked={noEndDate}
                  onChange={(event) => setNoEndDate(event.target.checked)}
                />
                {t('targetedMarketing.noEndDate')}
              </label>
            </div>
          </div>
          <div className={styles.field}>
            <label htmlFor="wt-topic-hour">{t('targetedMarketing.hourOfDay')}</label>
            <select
              id="wt-topic-hour"
              value={hourOfDay}
              onChange={(event: ChangeEvent<HTMLSelectElement>) => (
                setHourOfDay(Number(event.target.value))
              )}
            >
              {Array.from({ length: 24 }, (_, hour) => (
                <option key={hour} value={hour}>{formatHourOfDay(hour)}</option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label htmlFor="wt-topic-timezone">{t('targetedMarketing.timezone')}</label>
            <select
              id="wt-topic-timezone"
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
            >
              {SCHEDULE_TIMEZONES.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label htmlFor="wt-topic-frequency">{t('targetedMarketing.frequency')}</label>
            <select
              id="wt-topic-frequency"
              value={frequency}
              onChange={(event) => setFrequency(event.target.value)}
            >
              {SCHEDULE_FREQUENCIES.map((item) => (
                <option key={item} value={item}>
                  {t(`targetedMarketing.frequency_${item}`)}
                </option>
              ))}
            </select>
          </div>
          <label className={styles.field} htmlFor="wt-topic-paused">
            <span>{t('targetedMarketing.paused')}</span>
            <input
              id="wt-topic-paused"
              type="checkbox"
              checked={paused}
              onChange={(event) => setPaused(event.target.checked)}
            />
          </label>
        </div>
      ) : null}
    </Modal>
  );
}

export default WatchTowerCreateTopicDialog;
